import { useCallback, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel } from '@mui/material';

type Status = 'idle' | 'pending' | 'unknown' | 'success';

// The popup owner keeps this session alive while individual steps unmount.
export function usePmsBookingSession() {
  const [status, setStatus] = useState<Status>('idle');
  const statusRef = useRef<Status>('idle');
  const updateStatus = useCallback((next: Status) => {
    statusRef.current = next;
    setStatus(next);
  }, []);
  const beginSubmission = useCallback(() => {
    if (statusRef.current !== 'idle') return false;
    updateStatus('pending');
    return true;
  }, [updateStatus]);
  const canNavigate = useCallback(() => !['pending', 'unknown'].includes(statusRef.current), []);
  const reset = useCallback(() => {
    if (!canNavigate()) return false;
    updateStatus('idle');
    return true;
  }, [canNavigate, updateStatus]);
  return {
    status, updateStatus, canNavigate, reset,
    confirmationProps: { submissionStatus: status, beginSubmission, onSubmissionStatusChange: updateStatus },
    navigationProps: { navigationLocked: !canNavigate(), navigationGuard: canNavigate },
  };
}

export function PmsBookingRecovery({ session, practiceId, onRetry, onFound }: {
  session: ReturnType<typeof usePmsBookingSession>;
  practiceId: string | number;
  onRetry: () => void;
  onFound: () => void;
}) {
  const [verifiedAbsent, setVerifiedAbsent] = useState(false);
  if (session.status !== 'unknown') return null;
  return <Alert severity="warning" sx={{ m: 2 }}>
    The previous booking may have completed. Keep these patient details while checking the diary and patient record.
    <Button component="a" href={`/dashboard/practice/${practiceId}/scheduling?diary=1`} target="_blank" rel="noopener noreferrer">Open practice diary</Button>
    <Button onClick={() => { session.updateStatus('success'); onFound(); }}>Appointment found — close</Button>
    <FormControlLabel control={<Checkbox checked={verifiedAbsent} onChange={(event) => setVerifiedAbsent(event.target.checked)} />} label="I checked the diary and patient record and confirmed no appointment was created" />
    <Button disabled={!verifiedAbsent} onClick={() => { onRetry(); setVerifiedAbsent(false); session.updateStatus('idle'); }}>Allow another booking</Button>
  </Alert>;
}
